/* Learned routes on the console's map (2026-10-28): the same Google map and
   day / night colours as the live map, a pin at each route's start in its
   health colour, and the picked route drawn from its densest recent trip —
   a killed-app gap is a lifted pen, never a straight cut (splitRouteSegments).
   With no trail yet, a dashed line joins the two ends and says so. */
import React, { useEffect, useMemo, useRef } from 'react';
import { GoogleMap, OverlayView, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION } from '../utils/googleMapsLoader';
import { splitRouteSegments } from '../utils/routeSegments';
import { mapStyles } from './MapView';
import { LatLng, RouteProfile, routeCoords, routeHealth, routeName, toneHex } from './data/analytics';

const NAIROBI = { lat: -1.286, lng: 36.817 };
const centre = (w: number, h: number) => ({ x: -w / 2, y: -h / 2 });

const Dot: React.FC<{ fill: string; ring: string; size: number }> = ({ fill, ring, size }) => (
  <span style={{ display: 'block', width: size, height: size, borderRadius: '50%', background: fill, boxShadow: `0 0 0 3px ${ring}, 0 2px 6px rgba(17,21,32,.3)` }} />
);

export const RouteMap: React.FC<{
  routes: RouteProfile[];
  selected: RouteProfile | null;
  trail: (LatLng & { gapBefore?: boolean })[] | null;
  onSelect: (id: string) => void;
  dark: boolean;
}> = ({ routes, selected, trail, onSelect, dark }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useJsApiLoader({ id: GOOGLE_MAPS_SCRIPT_ID, googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES, version: GOOGLE_MAPS_VERSION });
  const mapRef = useRef<google.maps.Map | null>(null);
  const pins = useMemo(() => routes.map((p) => ({ p, c: routeCoords(p) })).filter((x): x is { p: RouteProfile; c: { start: LatLng; end: LatLng } } => !!x.c), [routes]);
  const sel = selected ? pins.find((x) => x.p.id === selected.id) || null : null;
  const tone = selected ? routeHealth(selected.effectivenessScore).tone : 'mute';
  const line = toneHex(tone, dark);

  const fitAll = () => {
    const m = mapRef.current;
    if (!m || !window.google || !pins.length) return;
    const b = new google.maps.LatLngBounds();
    pins.forEach((x) => { b.extend(x.c.start); b.extend(x.c.end); });
    m.fitBounds(b, 56);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fitAll(); }, [pins.length, isLoaded]);
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !window.google) return;
    if (trail && trail.length >= 2) {
      const b = new google.maps.LatLngBounds();
      trail.forEach((pt) => b.extend(pt));
      m.fitBounds(b, 48);
    } else if (sel) m.panTo(sel.c.start);
  }, [trail, sel]);
  useEffect(() => { mapRef.current?.setOptions({ styles: mapStyles(dark) }); }, [dark]);

  const shell: React.CSSProperties = { position: 'relative', width: '100%', height: '100%', background: dark ? '#141A1F' : '#F3F1EC' };
  if (!apiKey || loadError) {
    return (
      <div style={{ ...shell, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14, textAlign: 'center', padding: 24 }}>
        {!apiKey ? 'The map needs its Google Maps key on this site. The list works without it.' : 'The map could not load. The list works without it.'}
      </div>
    );
  }
  if (!isLoaded) return <div className="shim" style={{ ...shell, borderRadius: 0 }} aria-label="Loading map" />;
  if (!pins.length) {
    return <div style={{ ...shell, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14, padding: 24, textAlign: 'center' }}>Route ends appear here as drivers finish more trips.</div>;
  }

  return (
    <div style={shell} role="region" aria-label="Map of learned routes">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={NAIROBI}
        zoom={12}
        onLoad={(m) => { mapRef.current = m; m.setOptions({ styles: mapStyles(dark) }); fitAll(); }}
        onUnmount={() => { mapRef.current = null; }}
        options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', clickableIcons: false, styles: mapStyles(dark) }}
      >
        {sel && trail && trail.length >= 2 && splitRouteSegments(trail).map((seg, i) => (
          seg.length >= 2 ? <Polyline key={i} path={seg} options={{ strokeColor: line, strokeOpacity: 0.95, strokeWeight: 5 }} /> : null
        ))}
        {sel && (!trail || trail.length < 2) && (
          <Polyline path={[sel.c.start, sel.c.end]} options={{
            strokeOpacity: 0, icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.8, strokeColor: line, scale: 3 }, offset: '0', repeat: '14px' }],
          }} />
        )}
        {pins.map(({ p, c }) => {
          const on = sel?.p.id === p.id;
          const t = routeHealth(p.effectivenessScore).tone;
          return (
            <OverlayView key={p.id} position={c.start} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
              <button type="button" onClick={() => onSelect(p.id)} title={`${routeName(p)} · ${p.effectivenessScore}`}
                aria-label={`${routeName(p)}, score ${p.effectivenessScore}`}
                style={{ all: 'unset', cursor: 'pointer', display: 'block', padding: 4, zIndex: on ? 3 : 1, position: 'relative' }}>
                <Dot fill={toneHex(t, dark)} ring={on ? (dark ? '#E9EDF0' : '#111520') : '#fff'} size={on ? 16 : 12} />
              </button>
            </OverlayView>
          );
        })}
        {sel && (
          <OverlayView position={trail && trail.length >= 2 ? trail[trail.length - 1] : sel.c.end} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
            <span title={sel.p.endLabel || 'End'} style={{ display: 'block', padding: 4 }}><Dot fill={dark ? '#E9EDF0' : '#111520'} ring="#fff" size={11} /></span>
          </OverlayView>
        )}
      </GoogleMap>
      <div style={{ position: 'absolute', left: 10, bottom: 10, display: 'flex', gap: 12, flexWrap: 'wrap', background: 'var(--s1)', border: '1px solid var(--line)', borderRadius: 10, padding: '6px 10px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)' }}>
        {[85, 70, 50, 20].map((s) => {
          const h = routeHealth(s);
          return <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ width: 8, height: 8, borderRadius: '50%', background: toneHex(h.tone, dark), display: 'block' }} />{h.label}</span>;
        })}
      </div>
    </div>
  );
};

/** One trip drawn on the map (2026-10-28, the classic bus page's run map): its
    trail in ink, a lifted pen across a gap, the start ringed and the end filled. */
export const TripMap: React.FC<{ path: (LatLng & { gapBefore?: boolean })[] | null; dark: boolean }> = ({ path, dark }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useJsApiLoader({ id: GOOGLE_MAPS_SCRIPT_ID, googleMapsApiKey: apiKey, libraries: GOOGLE_MAPS_LIBRARIES, version: GOOGLE_MAPS_VERSION });
  const mapRef = useRef<google.maps.Map | null>(null);
  const ink = dark ? '#E9EDF0' : '#111520';
  const fit = () => {
    const m = mapRef.current;
    if (!m || !window.google || !path || path.length < 2) return;
    const b = new google.maps.LatLngBounds();
    path.forEach((pt) => b.extend(pt));
    m.fitBounds(b, 40);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fit(); }, [path, isLoaded]);
  useEffect(() => { mapRef.current?.setOptions({ styles: mapStyles(dark) }); }, [dark]);

  const shell: React.CSSProperties = { position: 'relative', width: '100%', height: '100%', background: dark ? '#141A1F' : '#F3F1EC' };
  const note = (text: string) => <div style={{ ...shell, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14, textAlign: 'center', padding: 24 }}>{text}</div>;
  if (!apiKey || loadError) return note(!apiKey ? 'The map needs its Google Maps key on this site.' : 'The map could not load.');
  if (!isLoaded || path === null) return <div className="shim" style={{ ...shell, borderRadius: 0 }} aria-label="Loading the trip" />;
  if (path.length < 2) return note('This trip has no GPS trail to draw.');
  return (
    <div style={shell} role="region" aria-label="Map of this trip">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={path[0]}
        zoom={13}
        onLoad={(m) => { mapRef.current = m; m.setOptions({ styles: mapStyles(dark) }); fit(); }}
        onUnmount={() => { mapRef.current = null; }}
        options={{ disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', clickableIcons: false, styles: mapStyles(dark) }}
      >
        {splitRouteSegments(path).map((seg, i) => (
          seg.length >= 2 ? <Polyline key={i} path={seg} options={{ strokeColor: ink, strokeOpacity: 0.9, strokeWeight: 5 }} /> : null
        ))}
        <OverlayView position={path[0]} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
          <span title="Start" style={{ display: 'block', padding: 4 }}><Dot fill="#fff" ring={ink} size={12} /></span>
        </OverlayView>
        <OverlayView position={path[path.length - 1]} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={centre}>
          <span title="End" style={{ display: 'block', padding: 4 }}><Dot fill={ink} ring="#fff" size={12} /></span>
        </OverlayView>
      </GoogleMap>
    </div>
  );
};
